import React, { FC, useEffect, useCallback, useState, useMemo } from "react"
import { observer } from "mobx-react-lite"
import { TextStyle, Appearance, ViewStyle, TouchableOpacity, Alert } from "react-native"
import {
  Screen,
  Text,
  FlatList,
  ListRenderItem,
  JalaIcon,
  Button,
  FormControl,
  FormControlProps,
  ModelPicker,
  ModelPickerProps,
  NumeralInput
} from "../../../../components"
import { useStores } from "../../../../models"
import { color, nativeBaseTheme } from "../../../../theme"
import { CompositeScreenProps } from "@react-navigation/native"
import { ScrollView, View } from "native-base"
import { translate } from "../../../../i18n"
import moment from "moment"
import numeral from "numeral"
import _ from "lodash"
import { navigate } from "../../../../navigators"
import { ShrimpPrice } from "../../../../database"

const ROOT: ViewStyle = {
  flex: 1,
}

const SCROLL_VIEW: ViewStyle = {
  flexGrow: 1
}

const TITLE_TEXT: TextStyle ={
  marginLeft: 16,
  marginTop: 20,
  marginBottom: 12
}

const HEADER_CONTAINER = (isValue): ViewStyle => ({
  backgroundColor: isValue ? color.palette.white : color.palette.gray100,
  borderBottomWidth: 1,
  borderTopWidth: 1,
  borderColor: color.palette.lineBase,
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingVertical: 8,
  paddingHorizontal: 16
})

const DISCLAIMER_TEXT: TextStyle = {
  marginHorizontal: 12,
  marginTop: 10
}

const HIDDEN_BUTTON: ViewStyle = {
  paddingHorizontal: 16,
  flexDirection: 'row',
  alignItems: 'center',
  paddingVertical: 10
}

const DROPDOWN_INSIDE: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  width: '90%'
}

const PRICE_TAB_CONTAINER = (isLeft): ViewStyle => ({
  width: isLeft ? '25%' : '75%',
  alignItems: 'center'
})

const BORDER: ViewStyle = {
  borderBottomColor: color.palette.gray200,
  borderBottomWidth: 1
}

const BUTTON_CONTAINER: ViewStyle = {
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingHorizontal: 20,
  marginVertical: 20
}

const FORM_CONTROL: ViewStyle = {
  paddingHorizontal: 16,
  marginBottom: 8
}

const ModelField = (
  { name, colorScheme, formControlProps, modelPickerProps }:
  { name?: string, colorScheme?: "light" | "dark", formControlProps?: FormControlProps, modelPickerProps?: (Omit<ModelPickerProps, "onValueChange"> & { onValueChange?: (value, index, item) => void }) },
) => (
  <FormControl
    // labelTx={`waterQualityMultiMeasurementForm.${_.camelCase(name)}` as TxKeyPath}
    colorScheme={colorScheme}
    // style={FORM_DROPDOWN}
    {...formControlProps}
  >
     <ModelPicker
        colorScheme={colorScheme}
        placeholderTx="simulationScreen.chooseLocation"
        {...modelPickerProps}
      />
  </FormControl>
)

export const ItemHeader: FC<{ colorScheme?: "light"|"dark", title?: string, width?: string, alignItems?: string, isHeader?: boolean }> = observer(function ItemHeader(props) {
  const { colorScheme, title, width, alignItems, isHeader } = props
  const presetUsed = isHeader ? "title5" : "body3"
  return (
  <View width={width} alignItems={alignItems}>
    <Text color={color.palette.gray800} colorScheme={colorScheme} text={title} preset={presetUsed} numberOfLines={1} />
  </View>
  )
})

const HeaderTab: FC<{ colorScheme?: "light"|"dark" }> = observer(function HeaderTab(props) {
  return (
    <View style={HEADER_CONTAINER(false)}>
      <ItemHeader title={translate("simulationScreen.DateDOCHeader")} width="30%" isHeader />
      <ItemHeader title={translate("simulationScreen.Size")} width="15%" isHeader />
      <ItemHeader title={translate("simulationScreen.biomassHeader")} width="25%" alignItems="center" isHeader />
      <ItemHeader title={translate("simulationScreen.priceHeader")} width="30%" alignItems="flex-end" isHeader />
    </View>
  )
})

const ValueTab: FC<{ colorScheme?: "light"|"dark", data?: any }> = observer(function ValueTab(props) {
  const { data } = props
  const {
    item: { age, date, mass, size, totalPrice }
  } = data;
  return (
    <View style={HEADER_CONTAINER(true)}>
      <ItemHeader title={`${moment(date).format('DD MMM YYYY')} (${age})`} width="30%" />
      <ItemHeader title={numeral(size).format('0,0')} width="15%" />
      <ItemHeader title={numeral(mass).format('0,0')} width="25%" alignItems="center" />
      <ItemHeader title={`${numeral(totalPrice).format('0,0')}`} width="30%" alignItems="flex-end" />
    </View>
  )
})

const FooterList: FC<{ colorScheme?: "light"|"dark", data?: any }> = observer(function ValueTab(props) {
  const { data } = props
  return (
    <View style={HEADER_CONTAINER(true)}>
      <ItemHeader title={translate("overviewScreen.total")} width="45%" isHeader />
      <ItemHeader title={data && data.mass ? numeral(data.mass).format('0,0') : '0'} width="25%" alignItems="center" isHeader />
      <ItemHeader title={data && data.totalPrice ? numeral(data.totalPrice).format('0,0') : '0'} width="30%" alignItems="flex-end" isHeader />
    </View>
  );
})

export const BottomButton: FC<{ colorScheme?: "light"|"dark", onSave?: () => void, onBack?: () => void, disabled?: boolean }> = observer(function ValueTab(props) {
  const { colorScheme, onSave, onBack, disabled } = props
  return (
    <View>
      <View style={BORDER} />
      <View style={BUTTON_CONTAINER}>
        <View width="48%">
          <Button
            testID="jala:id/simulation-result-back"
            tx="edit"
            colorScheme={colorScheme}
            onPress={onBack}
            preset="outline"
            disabled={disabled}
          />
        </View>
        <View width="48%">
          <Button
            testID="jala:id/simulation-result-save"
            tx="save"
            colorScheme={colorScheme}
            onPress={onSave}
            disabled={disabled}
          />
        </View>
      </View>
    </View>
  )
})

export const SimulationHarvestScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function SimulationHarvestScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const {
    simulationStore,
    simulationStore: { simulation, prices, selectedRegion }
  } = useStores()

  const [ simulationResult, setSimulationResult ] = useState<any>({})
  const [ priceShow, setPriceShow ] = useState<boolean>(false)
  // const [ selectedPrice, setselectedPrice ] = useState<any>(null)
  // const [ prices, setPrices ] = useState<any>([])
  const [ isSaving, setIsSaving ] = useState<boolean>(false)

  const initData = useCallback(async () => {
    await simulationStore.calculateSimulation(simulation).then((response) => {
      simulationStore.set('prices', response.prices.reverse())
      simulationStore.set('selectedRegion', null)
      if (simulation.shrimpPrice && simulation.shrimpPrice.id) simulationStore.set('selectedRegion', response.shrimpPriceId)
      setSimulationResult(response)
    })
  }, [simulation, simulationStore])

  useEffect(() => {
    initData().catch()
  }, [initData])

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <ValueTab data={info} colorScheme={colorScheme} />
  }, [colorScheme])

  const onSave = useCallback(() => {
    setIsSaving(true)
    return simulation.id
      ? simulationStore.editSimulation().then((res) => {
        if(res.status === 200 || res.status === 201 ) navigate('App.Stack', 'Home.Stack', { screen: 'Home.Simulation.List' })
        else Alert.alert('Failed', '')
      })
      : simulationStore.saveSimulation().then((res) => {
        if (res.status === 200 || res.status === 201) navigate('App.Stack', 'Home.Stack', { screen: 'Home.Simulation.List' })
        else Alert.alert('Failed', '')
      });
  }, [simulation, simulationStore])

  const onBack = useCallback(() => navigate('App.Stack', 'Home.Stack', { screen: 'Home.Simulation.Create', params: 'isEdit' }), [])

  const onChangePrice = useCallback(async (value) => {
    await ShrimpPrice.fetch({
      id__eq: value
    }).then(async (res) => {
      const price = res[0]
      const prices = simulationResult.defaultPrices.map(({ size: s }) => ({
        size: s,
        price: price[`size_${s}`] || ''
      }));
      await simulationStore.calculateSimulation({
        ...simulationResult,
        shrimpPriceId: value,
        prices,
        currencyId: price.currency_id || ''
      }).then((response) => setSimulationResult(response))
      simulationStore.set('prices', prices.reverse())
      simulationStore.set('selectedRegion', value)
    })
  }, [simulationResult, simulationStore])

  const updateSimulationResultByPrices = useMemo(() => _.debounce(async (prices) => {
    const ps = simulationResult.prices.map(({ size }) => ({
      size,
      price: prices.find((p) => p.size === size).price || null
    }));
    await simulationStore.calculateSimulation({
      ...simulationResult,
      shrimpPriceId: null,
      prices: ps
    }).then((response) => setSimulationResult(response))
  }, 1500, { leading: false, trailing: true }), [simulationResult, simulationStore]);

  const onChangeInput = useCallback((size) => (text) => {
    const value = numeral(text).value() || 0;
    const ps = prices.map(({ size: s, price }) => ({
      size: s,
      price: (size === s && value) || (size !== s && price) || ''
    }));
    simulationStore.set('prices', ps)
    updateSimulationResultByPrices(ps);
  }, [prices, simulationStore, updateSimulationResultByPrices])

  const showDisclaimer = !simulationResult.totalHarvest || !simulationResult.totalHarvest.totalPrice;
  const separatorIcon = priceShow ? 'arrow_drop_up' : 'arrow_drop_down';
  const isLocNull = selectedRegion === null
  return (
    <Screen
      style={ROOT}
      preset="fixed"
      colorScheme={colorScheme}
      backgroundColor={colorScheme === "light" ? color.palette.white : undefined}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === "light" ? "light-content" : "dark-content"}
    >
      <ScrollView contentContainerStyle={SCROLL_VIEW}>
        <Text preset="title3" tx="simulationScreen.TotalHarvest" colorScheme={colorScheme} style={TITLE_TEXT} color={color.palette.gray800} />
        <HeaderTab />
        <View>
          <FlatList
            data={simulationResult.harvests}
            renderItem={renderItem}
            ListFooterComponent={<FooterList data={simulationResult.totalHarvest} />}
            extraData={prices}
            estimatedItemSize={67}
          />
        </View>
        {showDisclaimer && <Text style={DISCLAIMER_TEXT} preset="label2" tx="simulationScreen.HarvestPriceDisclaimer" color={color.palette.red600} />}
        <TouchableOpacity style={HIDDEN_BUTTON} onPress={() => setPriceShow(!priceShow)}>
          <Text preset="title4" color={color.primary} tx="simulationScreen.inputPrice" />
          <JalaIcon name={separatorIcon} size={17} color={color.primary} />
        </TouchableOpacity>
        {priceShow &&
          <View paddingLeft="16px" paddingRight="16px">
            <View flexDirection="row" alignItems="center" justifyContent="space-between">
              <Text preset="title3" tx="simulationScreen.priceSimulation" color={color.palette.gray800} />
            </View>
            {isLocNull && (
              <View flexDirection="row" alignItems="center" paddingLeft={2}>
                <JalaIcon name="info-fill" size={12} color={color.palette.red} />
                <Text preset="label2" tx="simulationScreen.locationDisclaimer" color={color.palette.red} />
              </View>
            )}
            <View marginTop={15}>
              <Text preset="body2" tx="simulationScreen.location" color={color.palette.darkGrey} />
              <ModelField
                colorScheme={colorScheme}
                name='shrimp_price_id'
                modelPickerProps={{
                  value: selectedRegion,
                  onValueChange: onChangePrice,
                  Model: ShrimpPrice,
                  // dataLabel: "size_100",
                  fetchData: (model, search) => (
                    model.fetch({
                      search,
                      with: 'region',
                      sort: "date,desc",
                      per_page: 100
                    })
                  ),
                  pickerActionsheetProps: {
                    renderItem({ item }) {
                      const regionName = item && item.region && item.region.full_name || '-'
                      const background = item && selectedRegion && item.id === selectedRegion ? nativeBaseTheme.colors.gray[colorScheme === 'light' ? "100" : "900"] : undefined
                      const date = item ? moment(item.date).format('YYYY-MM-DD') : '-'
                      return <View paddingY="12px" paddingX="16px" backgroundColor={background}>
                        <View flexDirection="row">
                          <Text text={date} preset="body2" colorScheme={colorScheme} />
                          <Text text={` - ${regionName}`} preset="body2" colorScheme={colorScheme} />
                        </View>
                      </View>
                    },
                  },
                  renderSelectedItemText: (item) => {
                    const regionName = item && item.region && item.region.full_name || '-'
                    return item && `${moment(item.date).format('YYYY-MM-DD')} - ${regionName}`
                  },
                }}
              />
              <View style={DROPDOWN_INSIDE}>
                <View style={PRICE_TAB_CONTAINER(true)}>
                  <Text preset="title4" color={color.palette.darkGrey} tx="simulationScreen.Size" />
                </View>
                <View style={PRICE_TAB_CONTAINER(false)}>
                  <Text preset="title4" color={color.palette.darkGrey} tx="homeScreen.price" />
                </View>
              </View>
              {prices.map((price, index) => (
                <View key={`${index}-${selectedRegion}`} flexDirection="row" alignItems="center" justifyContent="space-between" >
                  <View width="20%" alignItems="center">
                    <Text text={price.size} preset="title4" color={color.palette.darkGrey} />
                  </View>
                  <View flex={1}>
                    <FormControl
                      colorScheme={colorScheme}
                      inputProps={{
                        autoCapitalize: "none",
                        autoComplete: "off",
                        autoCorrect: false,
                        textContentType: "none",
                        keyboardType: "numeric",
                        returnKeyType: "go",
                        defaultValue: price.price || 0,
                        // onSubmitEditing: () => onValueChange(value),
                      }}
                      style={FORM_CONTROL}
                    >
                      {(props) => (
                        <NumeralInput
                          colorScheme={colorScheme}
                          onChangeNumber={onChangeInput(price.size)}
                          placeholder={translate("simulationScreen.inputPriceTag")}
                          {...props}
                        />
                      )}
                    </FormControl>
                  </View>
                </View>
              ))}
            </View>
          </View>
        }
      </ScrollView>
      <BottomButton onSave={onSave} onBack={onBack} colorScheme={colorScheme} disabled={isSaving} />
    </Screen>
  )
})
